/**
 * PR Monitoring Hooks for SWRM-30
 * 
 * Provides comprehensive hooks for PR monitoring functionality including:
 * - Individual PR status monitoring
 * - Active monitoring sessions management
 * - PR management operations (retry, override, escalate)
 * - Real-time WebSocket updates
 * - Dashboard analytics
 */

import { useState, useEffect, useCallback } from 'react';
import {
  PRWorkflowStatus,
  PRMonitoringSession,
  PRMonitoringDashboard,
  PRBlockingAnalytics,
  PROperationResponse,
  WebSocketMessage
} from '../types';
import { useWebSocket } from './useWebSocket';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8002';

/**
 * Hook for managing PR status for a specific workflow
 */
export const usePRStatus = (workflowId: number | null) => {
  const [prStatus, setPRStatus] = useState<PRWorkflowStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // WebSocket integration for real-time updates
  const { lastMessage } = useWebSocket();

  const fetchPRStatus = useCallback(async () => {
    if (!workflowId) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/pr-monitoring/status/${workflowId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          setPRStatus(null);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setPRStatus(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch PR status';
      setError(errorMessage);
      console.error('Error fetching PR status:', err);
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  // Handle real-time WebSocket updates
  useEffect(() => {
    if (lastMessage && workflowId) {
      const message = lastMessage as WebSocketMessage;
      
      if (message.type === 'pr_status_update' && message.data.workflow_id === workflowId) {
        // Update PR status from WebSocket
        fetchPRStatus();
      } else if (message.type === 'pr_blocking_update' && message.data.workflow_id === workflowId) {
        // Refresh status on blocking updates
        fetchPRStatus();
      } else if (message.type === 'pr_monitoring_retry' && message.data.workflow_id === workflowId) {
        // Refresh status after retry
        fetchPRStatus();
      } else if (message.type === 'pr_blocking_override' && message.data.workflow_id === workflowId) {
        // Refresh status after override
        fetchPRStatus();
      }
    }
  }, [lastMessage, workflowId, fetchPRStatus]);

  // Fetch initial data
  useEffect(() => {
    if (workflowId) {
      fetchPRStatus();
    }
  }, [workflowId, fetchPRStatus]);

  return {
    prStatus,
    loading,
    error,
    refetch: fetchPRStatus,
  };
};

/**
 * Hook for managing active PR monitoring sessions
 */
export const usePRMonitoringSessions = () => {
  const [sessions, setSessions] = useState<PRMonitoringSession[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // WebSocket integration for real-time updates
  const { lastMessage } = useWebSocket();

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/pr-monitoring/active`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSessions(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch monitoring sessions';
      setError(errorMessage);
      console.error('Error fetching monitoring sessions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle real-time WebSocket updates
  useEffect(() => {
    if (lastMessage) {
      const message = lastMessage as WebSocketMessage;
      
      if (['pr_status_update', 'pr_monitoring_retry', 'pr_escalation'].includes(message.type)) {
        // Refresh sessions on relevant updates
        fetchSessions();
      }
    }
  }, [lastMessage, fetchSessions]);

  // Fetch initial data
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  return {
    sessions,
    loading,
    error,
    refetch: fetchSessions,
  };
};

/**
 * Hook for PR monitoring dashboard data
 */
export const usePRMonitoringDashboard = () => {
  const [dashboard, setDashboard] = useState<PRMonitoringDashboard | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // WebSocket integration for real-time updates
  const { lastMessage } = useWebSocket();

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/pr-monitoring/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setDashboard(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch dashboard data';
      setError(errorMessage);
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle real-time WebSocket updates
  useEffect(() => {
    if (lastMessage) {
      const message = lastMessage as WebSocketMessage;
      
      // Refresh dashboard on any PR monitoring updates
      if (message.type.startsWith('pr_')) {
        fetchDashboard();
      }
    }
  }, [lastMessage, fetchDashboard]);

  // Fetch initial data
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return {
    dashboard,
    loading,
    error,
    refetch: fetchDashboard,
  };
};

/**
 * Hook for PR management actions
 */
export const usePRActions = () => {
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const performAction = async (
    action: 'retry' | 'override' | 'escalate',
    workflowId: number,
    payload?: { reason?: string; level?: number }
  ): Promise<PROperationResponse> => {
    setActionLoading(true);
    setActionError(null);

    try {
      const token = localStorage.getItem('token');
      const url = `${API_BASE_URL}/api/pr-monitoring/${workflowId}/${action}`;
      
      const requestOptions: RequestInit = {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };

      if (payload) {
        requestOptions.body = JSON.stringify(payload);
      }

      const response = await fetch(url, requestOptions);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to ${action} PR monitoring`;
      setActionError(errorMessage);
      console.error(`Error performing ${action}:`, err);
      return { success: false, error: errorMessage };
    } finally {
      setActionLoading(false);
    }
  };

  const retryPRMonitoring = useCallback(
    (workflowId: number) => performAction('retry', workflowId),
    []
  );

  const overridePRBlocking = useCallback(
    (workflowId: number, reason: string) => 
      performAction('override', workflowId, { reason }),
    []
  );

  const escalatePRIssue = useCallback(
    (workflowId: number, level: number, reason: string) => 
      performAction('escalate', workflowId, { level, reason }),
    []
  );

  return {
    retryPRMonitoring,
    overridePRBlocking,
    escalatePRIssue,
    actionLoading,
    actionError,
  };
};

/**
 * Hook for PR history data
 */
export const usePRHistory = (prNumber: number | null, repository: string | null) => {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!prNumber || !repository) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/pr-monitoring/history/${prNumber}?repository=${encodeURIComponent(repository)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setHistory(data.history || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch PR history';
      setError(errorMessage);
      console.error('Error fetching PR history:', err);
    } finally {
      setLoading(false);
    }
  }, [prNumber, repository]);

  // Fetch data when parameters change
  useEffect(() => {
    if (prNumber && repository) {
      fetchHistory();
    }
  }, [prNumber, repository, fetchHistory]);

  return {
    history,
    loading,
    error,
    refetch: fetchHistory,
  };
};

/**
 * Hook for blocking analytics
 */
export const useBlockingAnalytics = () => {
  const [analytics, setAnalytics] = useState<PRBlockingAnalytics | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/pr-monitoring/analytics/queue-depth`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch blocking analytics';
      setError(errorMessage);
      console.error('Error fetching blocking analytics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch initial data
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    analytics,
    loading,
    error,
    refetch: fetchAnalytics,
  };
};
